import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft,
  BarChart3,
  Users,
  Target,
  Clock,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  PieChart,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExamStatsPage() {
  const { workspaceId, examId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [stats, setStats] = useState(null);
  
  useEffect(() => {
    const init = async () => {
      if (workspaceId && examId) {
        await Promise.all([loadExam(workspaceId), loadStats(workspaceId)]);
      }
    };
    
    init();
  }, [workspaceId, examId]);
  
  const token = localStorage.getItem('token');
  const authHeaders = { 'Authorization': `Bearer ${token}` };

  const loadExam = async (wsId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/${examId}?workspace_id=${wsId}&include_questions=false`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setExam(data);
      }
    } catch (error) {
      console.error('Failed to load exam:', error);
    }
  };
  
  const loadStats = async (wsId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/${examId}/stats?workspace_id=${wsId}`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        toast.error('Erreur lors du chargement des statistiques');
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </WorkspaceLayout>
    );
  }
  
  const scoreDistribution = stats?.score_distribution || {};
  const maxDistribution = Math.max(...Object.values(scoreDistribution), 1);
  
  return (
    <WorkspaceLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/exams/${examId}`)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <BarChart3 className="h-7 w-7 text-primary" />
                Statistiques
              </h1>
              <p className="text-muted-foreground">{exam?.title}</p>
            </div>
          </div>
          
          <Button variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/exams/${examId}/attempts`)}>
            <Users className="h-4 w-4 mr-2" />
            Voir les tentatives
          </Button>
        </div>
        
        {/* Main Stats */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total tentatives</p>
                  <p className="text-2xl font-bold">{stats?.total_attempts || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                  <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Score moyen</p>
                  <p className="text-2xl font-bold">{stats?.average_score?.toFixed(1) || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Taux de réussite</p>
                  <p className="text-2xl font-bold">{stats?.pass_rate?.toFixed(1) || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <Clock className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Durée moyenne</p>
                  <p className="text-2xl font-bold">{stats?.average_duration_minutes?.toFixed(0) || 0} min</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Score Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Distribution des scores
            </CardTitle>
            <CardDescription>
              Répartition des scores par tranches
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.total_attempts === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Aucune donnée disponible</p>
                <p className="text-sm">Les statistiques apparaîtront après les premières tentatives</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(scoreDistribution).map(([range, count]) => {
                  const percentage = maxDistribution > 0 ? (count / maxDistribution) * 100 : 0;
                  const isPassingRange = ['61-80', '81-100'].includes(range);
                  
                  return (
                    <div key={range} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{range}%</span>
                        <span className="text-muted-foreground">{count} candidat{count > 1 ? 's' : ''}</span>
                      </div>
                      <div className="h-8 bg-muted rounded-lg overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-500 ${
                            isPassingRange ? 'bg-green-500' : 'bg-orange-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Performance Summary */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                Réussites
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className="text-5xl font-bold text-green-600 mb-2">
                  {stats?.total_attempts > 0 
                    ? Math.round((stats.pass_rate / 100) * stats.total_attempts) 
                    : 0
                  }
                </div>
                <p className="text-muted-foreground">
                  candidat{stats?.total_attempts > 1 ? 's' : ''} ont réussi l'examen
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-red-500" />
                Échecs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className="text-5xl font-bold text-red-600 mb-2">
                  {stats?.total_attempts > 0 
                    ? stats.total_attempts - Math.round((stats.pass_rate / 100) * stats.total_attempts) 
                    : 0
                  }
                </div>
                <p className="text-muted-foreground">
                  candidat{stats?.total_attempts > 1 ? 's' : ''} n'ont pas atteint le score requis
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Actions */}
        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/exams/${examId}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux détails
          </Button>
          <Button variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/exams/${examId}/attempts`)}>
            <Users className="h-4 w-4 mr-2" />
            Voir toutes les tentatives
          </Button>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
